import { FC, Fragment, useCallback, useEffect, useMemo, useRef } from 'react'

import { Color } from 'constants/assets'
import { Nullable } from 'models/helpers'
import ReactLoading from 'react-loading'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { createPortal } from 'react-dom'
import { fadeInEnterEffect } from 'utils/animations'
import { logger } from 'utils/logging'
import { parseFramableUrl } from './chargebeeUrl'
import styles from './ChargebeeModal.module.sass'

interface Props {
  /** Title for iframe */
  title: string
  /** Url to provide for iframe src */
  hostedPageUrl: Nullable<string>
  /** Wheter modal should be open */
  isOpen: boolean
  /** Callback handler for closing modal (called on backdrop click) */
  handleClose?: () => void
  /** If provided and true, shows loading spinner */
  isLoading?: boolean
  /** Callback triggered on iframe onLoad event */
  onIframeLoaded?: () => void
}

/**
 * Renders iframe for given url, styled as a modal window for chargebee subscription management and checkout hosted pages.
 * Rendered as a Portal
 * @example
 * <ChargebeeModal
 *   title="Sunscribtion checkout"
 *   hostedPageUrl="https://some-valid-url"
 *   isOpen={isOpen}
 *   handleClose={() => setIsOpen(false)}
 * /> 
 */
export const ChargebeeModal: FC<Props> = ({
  title,
  hostedPageUrl,
  isOpen,
  isLoading = false,
  handleClose = () => undefined,
  onIframeLoaded = () => undefined,
}) => {

  const safeUrl = useMemo(() => parseFramableUrl(hostedPageUrl), [hostedPageUrl])

  // In a ref so the effect below runs per URL, not per inline-arrow identity change
  const handleCloseRef = useRef(handleClose)
  handleCloseRef.current = handleClose

  // A refused URL renders nothing, so the caller must be told to close or its button hangs
  useEffect(() => {
    if (!isOpen || !hostedPageUrl || safeUrl) return

    logger.error('Refused to frame a hosted page URL', new Error('Hosted page URL is not an absolute https URL'), {
      context: {
        component: 'ChargebeeModal',
        action: 'validateHostedPageUrl',
        metadata: { title, scheme: hostedPageUrl.split(':')[0] },
      },
    })
    handleCloseRef.current()
  }, [isOpen, hostedPageUrl, safeUrl, title])

  // Only the framed page may close us — the app is embeddable, so any parent could otherwise
  const handleMessageClose = useCallback((message: MessageEvent) => {
    if (!safeUrl || message.origin !== safeUrl.origin) return

    if (message.data === 'cb.close') handleClose()
  }, [handleClose, safeUrl])

  // Handle adding/removing of event listener for handling closing of chargebee modal
  useEffect(() => {
    if (!isOpen) return

    window.addEventListener('message', handleMessageClose)

    return () => window.removeEventListener('message', handleMessageClose)

  }, [handleMessageClose, isOpen])

  return createPortal((
    <Fragment>
      {isOpen && !!safeUrl &&
        <TransitionAppear
          onEnter={fadeInEnterEffect}
          visible
        >
          <div className={styles.backdrop} onClick={handleClose}>

            {isLoading &&
              <ReactLoading className={styles.loading} color={Color.WHITE} type="spin" />
            }

            <iframe
              title={title}
              src={safeUrl.href}
              referrerPolicy="no-referrer"
              onLoad={onIframeLoaded}
            />

          </div>
        </TransitionAppear>
      }
    </Fragment>
  ), document.querySelector('.App') || document.getElementById('root') || document.body)

}
